"use client";

import React, { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

interface FAQItem {
  id?: number | string;
  question: string;
  answer: string;
}

interface Props {
  faqs: FAQItem[];
  categoryName?: string;
}

const InnerPageFAQs = ({ faqs, categoryName }: Props) => {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  if (!faqs?.length) return null;

  return (
    <div className="w-full" itemScope itemType="https://schema.org/FAQPage">

      {/* Header */}
      <div className="flex items-center gap-2 pb-3 mb-3.5 border-b border-[#EADDCA]">
        <HelpCircle size={14} className="text-[#800000] shrink-0" />
        <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-[#1A1A1A] m-0">
          {categoryName ? `${categoryName} FAQs` : "FAQs"}
        </h3>
      </div>

      {/* Accordion */}
      <div className="space-y-2">
        {faqs.map((faq, i) => {
          const isOpen = openIdx === i;
          return (
            <div
              key={faq.id ?? i}
              className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                isOpen
                  ? "bg-[#800000]/[0.03] border-[#800000]/30 shadow-sm"
                  : "border-[#EADDCA]/60 bg-white hover:border-[#EADDCA]"
              }`}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? null : i)}
                className="w-full flex items-center justify-between gap-2.5 px-3.5 py-3 text-left bg-transparent border-none cursor-pointer group select-none"
                aria-expanded={isOpen}
              >
                <span
                  className={`text-xs font-bold leading-snug transition-colors duration-200 flex-1 ${
                    isOpen
                      ? "text-[#800000]"
                      : "text-[#1A1A1A] group-hover:text-[#800000]"
                  }`}
                  itemProp="name"
                >
                  {faq.question}
                </span>

                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                    isOpen
                      ? "bg-[#800000] text-white rotate-180"
                      : "bg-[#FDFBE7] text-slate-500 group-hover:bg-[#800000]/10 group-hover:text-[#800000]"
                  }`}
                >
                  <ChevronDown size={13} />
                </div>
              </button>

              {/* Answer */}
              <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                  isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <div className="overflow-hidden">
                  <div className="px-3.5 pb-3.5 pt-2 text-xs text-slate-500 border-t border-[#EADDCA]/60 leading-relaxed prose prose-xs max-w-none">
                    <div
                      itemProp="text"
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InnerPageFAQs;
